import React, { useState, useEffect, useContext } from 'react';
import { Line } from 'react-chartjs-2';
import MarketDataKeysMaster from '/src/services/ProcessingHousingMkt/MarketDataKeys.json';
import { WindowSizeContext } from '/src/App';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import './style.css';
import { AreaDataContext } from '/src/Views/PropertyExplorer/PropertyExplorer';
// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    annotationPlugin
);

const TheSignals = ({ housingMarketData, propertyData, disasterDeclarations, title, disasterDeclarationsState }) => {
    const { geoData } = useContext(AreaDataContext);
    const { deviceType } = useContext(WindowSizeContext);
    // Filter the disasterDeclarations and disasterDeclarationsState to only include years 2021 and later
    const filteredDisasterDeclarations = disasterDeclarations?.filter(disaster => disaster.year >= 2021)
    const filteredDisasterDeclarationsState = disasterDeclarationsState?.filter(disaster => disaster.year >= 2021)

    console.log('filteredDisasterDeclarations', filteredDisasterDeclarations)
    const refinedFipsMarketData = housingMarketData?.refinedMarketData?.fipsHousingMarketData || []
    const refinedZipMarketData = housingMarketData?.refinedMarketData?.zipHousingMarketData || []

    const newMarketScores = housingMarketData?.refinedMarketData?.newMarketScores || {}
    const newMarketScoresDeferTo = newMarketScores?.deferTo || 'stateDisasters'
    const marketDatapointScores = newMarketScores?.allDatapoints || []


    // Handler to determine the score text and css class of a datapoint based on the score and defer to
    const getScoreTextAndClass = (score, deferTo) => {

        const stateDisastersThresholds = {
            low: .55,
            moderate: .8,
            high: 1
        }

        const countyDisastersThresholds = {
            low: .3,
            moderate: .7,
            high: 1
        }

        const thresholdsToUse = deferTo === 'stateDisasters' ? stateDisastersThresholds : countyDisastersThresholds;
        const geoCSSText = deferTo === 'stateDisasters' ? 'state' : 'county';
        const isScoreNegative = score < 0;

        const scoreAbsoluteValue = Math.abs(score);

        let scoreText = 'Low';
        let scoreClass = 'low';

        if (scoreAbsoluteValue < thresholdsToUse.low) {
            scoreText = 'Low';
            scoreClass = 'low';
        } else if (scoreAbsoluteValue < thresholdsToUse.moderate) {
            scoreText = 'Moderate';
            scoreClass = 'medium';
        } else {
            scoreText = 'High';
            scoreClass = 'high';
        }

        const scoreClassWithGeo = `${scoreClass} ${geoCSSText}`;

        return {
            isScoreNegative: isScoreNegative,
            scoreText: scoreText,
            scoreClass: scoreClassWithGeo,
            scoreToUse: scoreAbsoluteValue
        };
    };

    // Iterate through the MarketDataKeysMaster keys and for each find the associated datapoint in marketDatapointScores
    const marketDataKeysWithScores = MarketDataKeysMaster.map(key => {
        const datapoint = marketDatapointScores.find(datapoint => datapoint.id === key.id) || {}

        const whichScoresToHighlight = newMarketScoresDeferTo === 'stateDisasters' ? datapoint.stateDisasters : datapoint.countyDisasters || {};

        const datapointScore = whichScoresToHighlight?.totalPointsPercentage || 0;

        const { isScoreNegative, scoreText, scoreClass, scoreToUse } = getScoreTextAndClass(datapointScore, newMarketScores.deferTo);

        return {
            ...key,
            score: datapoint?.score,
            isScoreNegative: isScoreNegative,
            scoreText: scoreText,
            scoreClass: scoreClass,
            scoreToUse: scoreToUse
        }
    })

    // Sort the newMarketScoresWithLabels array by scoreToUse, descending
    const sortedMarketDataKeysWithScores = marketDataKeysWithScores.sort((a, b) => b.scoreToUse - a.scoreToUse);


    const disaster_declarations_list = disasterDeclarations || []

    const zip_code = propertyData.data.property_details.zip
    const zip_code_text = `Zip ${zip_code}`
    const county = propertyData.data.property_details.county
    // refinedFipsMarketData and refinedZipMarketData are arrays with objects with matching keys
    // Get the list of keys and labels from the refinedFipsMarketData, keeping them together in objects we can iterate over
    const marketDataKeys = refinedFipsMarketData.map(item => ({
        id: item.id,
        label: item.label
    }));


    const [selectedDataKey, setSelectedDataKey] = useState(sortedMarketDataKeysWithScores[0].id);
    const [selectedDataObject, setSelectedDataObject] = useState(sortedMarketDataKeysWithScores[0]);
    const [selectedFipsData, setSelectedFipsData] = useState(null);
    const [selectedZipData, setSelectedZipData] = useState(null);
    const [selectedGeoView, setSelectedGeoView] = useState('fips');
    const [selectedDisplayOption, setSelectedDisplayOption] = useState('yoy');
    const [selectedTwoYAxis, setSelectedTwoYAxis] = useState(false);
    const [showTrendLines, setShowTrendLines] = useState(false);


    const handleDataKeyChange = (key) => {
        setSelectedDataKey(key);
        setSelectedDataObject(sortedMarketDataKeysWithScores.find(item => item.id === key));
    }


    // When the handleDataKeyChanges use this to filter the fips and zip market data using that key as the id 
    useEffect(() => {
        if (refinedFipsMarketData && refinedZipMarketData) {
            const fipsData = refinedFipsMarketData.find(item => item.id === selectedDataKey);
            const zipData = refinedZipMarketData.find(item => item.id === selectedDataKey);

            // Filter data to start from 2021
            if (fipsData?.data) {
                fipsData.data = fipsData.data.filter(item => item.year >= 2021);
                // Check to make sure that the value for each item is not null
                const areThereNonNullValuesFIPS = fipsData.data.some(item => item.value !== null);
                // console.log(`areThereNonNullValues FIPS:`, areThereNonNullValuesFIPS)
                fipsData.data = fipsData.data.filter(item => item.value !== null);
                // console.log(`fipsData in the filtering:`, fipsData)
            }
            if (zipData?.data) {
                zipData.data = zipData.data.filter(item => item.year >= 2021);
                const areThereNonNullValuesZIP = zipData.data.some(item => item.value !== null);
                // console.log(`areThereNonNullValues ZIP:`, areThereNonNullValuesZIP)
                zipData.data = zipData.data.filter(item => item.value !== null);
                // console.log(`zipData in the filtering:`, zipData)
            }
            setSelectedTwoYAxis(zipData.twoYAxis)
            setSelectedFipsData(fipsData);
            setSelectedZipData(zipData);
        }
    }, [refinedFipsMarketData, refinedZipMarketData, selectedDataKey]);
    // console.log(`selectedTwoYAxis:`, selectedTwoYAxis)

    // Function to get color for a specific year using a sequential color scheme
    // const getColorForYear = (year, alpha = 1) => {
    //     // Create a sequential color scheme that progresses from cool to warm colors
    //     const colorMap = {
    //         '2020': `rgba(49, 54, 149, ${alpha})`,     // Deep blue
    //         '2021': `rgba(69, 117, 180, ${alpha})`,    // Blue
    //         '2022': `rgba(116, 173, 209, ${alpha})`,   // Light blue
    //         '2023': `rgba(171, 217, 233, ${alpha})`,   // Very light blue/cyan
    //         '2024': `rgba(253, 174, 97, ${alpha})`,    // Light orange
    //         '2025': `rgba(215, 25, 28, ${alpha})`      // Red
    //     };

    //     return colorMap[year] || `rgba(128, 128, 128, ${alpha})`;  // Default gray for unknown years
    // };


    // Function to get the correct dataset based on current selections
    const getActiveDataset = () => {
        // First determine if we're using county or zip data
        const selectedData = selectedGeoView === 'fips' ? selectedFipsData : selectedZipData;

        if (!selectedData) return null;
        const dataType = selectedData.dataType

        let activeDatasetSelection;
        // Determine which data format to use based on displayOption
        if (selectedData.displayOption === 'both') {
            // If displayOption is 'both', use the user's selection
            activeDatasetSelection = selectedDisplayOption === 'yoy'
                ? selectedData.yearlyData.filter(yearData => yearData.year > 2020)  // Filter yearly data
                : selectedData.data.filter(item => item.year > 2020);  // Filter regular data
        } else {
            // Otherwise use the format specified by the data itself
            activeDatasetSelection = selectedData.displayOption === 'yoy'
                ? selectedData.yearlyData.filter(yearData => yearData.year > 2020)  // Filter yearly data
                : selectedData.data.filter(item => item.year > 2020);  // Filter regular data
        }

        return {
            dataType: dataType,
            activeDatasetSelection: activeDatasetSelection
        }
    };

    // Get the active dataset for use in the component
    const activeDatasetInfo = getActiveDataset();
    const activeDataset = activeDatasetInfo?.activeDatasetSelection;
    const activeDataType = activeDatasetInfo?.dataType;


    // Use effect that updates the selectedDisplayOption by taking the geoView, looking at the selected data (based on fips or zip)
    // looking at the displayOption and if it is both then set selectedDisplayOption to 'yoy' else set it to null (unless already set as null)
    useEffect(() => {
        if (selectedGeoView === 'fips' && selectedFipsData?.displayOption === 'both') {
            setSelectedDisplayOption('yoy');
        } else if (selectedGeoView === 'zip' && selectedZipData?.displayOption === 'both') {
            setSelectedDisplayOption('yoy');
        } else if (selectedDisplayOption !== null) {
            setSelectedDisplayOption(null);
        }
    }, [selectedFipsData, selectedZipData]);


    // Determine if we're using yearly data based on the display option
    const isYearlyView = () => {
        const selectedData = selectedGeoView === 'fips' ? selectedFipsData : selectedZipData;

        if (!selectedData) return false;

        if (selectedData.displayOption === 'both') {
            // If displayOption is 'both', use the user's selection
            return selectedDisplayOption === 'yoy';
        } else {
            // Otherwise use the format specified by the data itself
            return selectedData.displayOption === 'yoy';
        }
    };

    // Use this function throughout the component
    const isYearlyData = isYearlyView();

    // Add formatValue helper function
    const formatValue = (value, dataType, tooltip = false) => {
        if (!value) return '';
        // console.log(`value: ${value} --> dataType: ${dataType}`)

        let ratioRound = 2
        if (tooltip) {
            ratioRound = 3
        }

        switch (dataType) {
            case 'dollar':
                const numValue = typeof value === 'string' ? parseFloat(value) : value;
                if (Math.abs(numValue) >= 1000000) {
                    return `$${Math.round(numValue / 1000000)}M`;
                } else if (Math.abs(numValue) >= 1000) {
                    return `$${Math.round(numValue / 1000)}K`;
                }
                return `$${Math.round(numValue)}`;

            case 'percentage':
                return `${Math.round(value * 100)}%`;

            case 'count':
                return value.toFixed(0);
            case 'ratio':
                return value.toFixed(ratioRound);
            default:
                return value.toFixed(2);
        }
    };

    // Add this helper function to calculate linear regression
    // const calculateTrendline = (data) => {
    //     console.log(`data handed to calculateTrendline:`, data)
    //     const n = data.length;
    //     let sumX = 0;
    //     let sumY = 0;
    //     let sumXY = 0;
    //     let sumXX = 0;

    //     // Calculate sums
    //     data.forEach((point, i) => {
    //         sumX += i;
    //         sumY += point;
    //         sumXY += i * point;
    //         sumXX += i * i;
    //     });

    //     // Calculate slope and y-intercept
    //     const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    //     const intercept = (sumY - slope * sumX) / n;
    //     console.log(`slope:`, slope)
    //     console.log(`intercept:`, intercept)

    //     // Generate trendline points
    //     return data.map((_, i) => slope * i + intercept);
    // };

    // Move sortedData preparation outside of both functions so it's available to both
    // const sortedData = activeDataset ? [...activeDataset].sort((a, b) => {
    //     return new Date(a.date) - new Date(b.date);
    // }) : [];

    const prepareChartData = () => {
        if (!selectedFipsData && !selectedZipData) {
            return { labels: [], datasets: [] };
        }

        // Sort both datasets
        const sortedFipsData = selectedFipsData?.data ? [...selectedFipsData.data].sort((a, b) => {
            return new Date(a.date) - new Date(b.date);
        }) : [];

        const sortedZipData = selectedZipData?.data ? [...selectedZipData.data].sort((a, b) => {
            return new Date(a.date) - new Date(b.date);
        }) : [];

        // Use county data as base for labels if available, otherwise use zip
        const sortedData = sortedFipsData.length > 0 ? sortedFipsData : sortedZipData;

        // Just use first letter of month
        const labels = sortedData.map(item => {
            const monthNames = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
            return monthNames[item.month - 1];
        });

        // Create year markers for x-axis
        const yearMarkers = sortedData.map((item, index) => {
            return (item.month === 1 || index === 0 ||
                (index > 0 && item.year !== sortedData[index - 1].year))
                ? item.year.toString()
                : null;
        });

        const datasets = [];

        // Add county data if available
        if (sortedFipsData.length > 0) {
            const fipsValues = sortedFipsData.map(item => item.value);
            // const fipsTrendline = calculateTrendline(fipsValues);
            const fipsTrendline = selectedFipsData.trend.trendLine;

            datasets.push(
                {
                    label: 'Los Angeles County',
                    data: fipsValues,
                    backgroundColor: 'rgba(75, 192, 192, .8)',
                    borderColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.3,
                    fill: false
                }
            );

            if (showTrendLines) {
                datasets.push(
                    {
                        label: 'Los Angeles County Trend',
                        data: fipsTrendline,
                        borderColor: 'rgba(75, 192, 192, 0.5)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        fill: false,
                        pointRadius: 0,
                        tension: 0
                    }
                );
            }
        }

        // Add zip data if available
        if (sortedZipData.length > 0) {
            const zipValues = sortedZipData.map(item => item.value);
            // const zipTrendline = calculateTrendline(zipValues);
            const zipTrendline = selectedZipData.trend.trendLine;

            datasets.push(
                {
                    label: `Zip ${zip_code}`,
                    data: zipValues,
                    backgroundColor: 'rgba(153, 102, 255, .8)',
                    borderColor: 'rgba(153, 102, 255, 0.2)',
                    tension: 0.3,
                    fill: false
                }
            );

            if (showTrendLines) {
                datasets.push(
                    {
                        label: `Zip ${zip_code} Trend`,
                        data: zipTrendline,
                        borderColor: 'rgba(153, 102, 255, 0.5)',
                        borderWidth: 2,
                        borderDash: [5, 5],
                        fill: false,
                        pointRadius: 0,
                        tension: 0
                    }
                );
            }
        }

        return {
            labels,
            datasets,
            yearMarkers
        };
    };

    const chartData = prepareChartData();

    // console.log(`chartData:`, chartData)
    // console.log(`selectedFipsData:`, selectedFipsData)
    // console.log(`selectedZipData:`, selectedZipData)
    const isThereFipsData = selectedFipsData?.data?.length > 0
    const isThereZipData = selectedZipData?.data?.length > 0
    // console.log(`isThereFipsData:`, isThereFipsData)
    // console.log(`isThereZipData:`, isThereZipData)

    let chart_title_text = ''
    let chart_legend_fips_text = null
    let chart_legend_fips_trend_text = null
    let chart_legend_zip_text = null
    let chart_legend_zip_trend_text = null
    if (isThereFipsData && isThereZipData) {
        chart_title_text = `${county} vs. Zip ${zip_code}`
        chart_legend_fips_text = `${county}`
        chart_legend_fips_trend_text = `${county} Trend`
        chart_legend_zip_text = `Zip ${zip_code}`
        chart_legend_zip_trend_text = `Zip ${zip_code} Trend`
    } else if (isThereFipsData) {
        chart_title_text = county
        chart_legend_fips_text = `${county}`
        chart_legend_fips_trend_text = `${county} Trend`
    } else if (isThereZipData) {
        chart_title_text = `Zip ${zip_code}`
        chart_legend_zip_text = `Zip ${zip_code}`
        chart_legend_zip_trend_text = `Zip ${zip_code} Trend`
    }

    const countyDisasterColor = 'rgb(255, 0, 0)'
    const stateDisasterColor = 'rgb(167, 167, 167)'

    const createDisasterAnnotations = (disasterDeclarationsList, type, yMin, yMax) => {
        const color = type === 'county' ? countyDisasterColor : stateDisasterColor;

        // Get the base dataset for reference
        const sortedData = selectedFipsData?.data ? [...selectedFipsData.data].sort((a, b) => {
            return new Date(a.date) - new Date(b.date);
        }) : [];

        const disasterAnnotations = disasterDeclarationsList?.map((disaster, index) => {
            if (disaster.year >= 2021) {
                // Find matching data point index
                const dataPointIndex = sortedData.findIndex(point =>
                    point.year === disaster.year &&
                    point.month === disaster.month
                );

                if (dataPointIndex !== -1) {
                    return {
                        [`disaster-${type}-${index}`]: {
                            type: 'line',
                            scaleID: 'x',
                            value: dataPointIndex,
                            borderColor: color,
                            borderWidth: 2,
                            yMin: yMin,
                            yMax: yMax,
                            label: {
                                enabled: false
                            }
                        }
                    };
                }
            }
            return null;
        }).filter(Boolean).reduce((acc, curr) => ({ ...acc, ...curr }), {});

        return disasterAnnotations;
    };


    let showCountyAndStateDisasters = true
    if (filteredDisasterDeclarations.length === 0) {
        showCountyAndStateDisasters = false
    }
    else if (newMarketScoresDeferTo === 'countyDisasters') {
        showCountyAndStateDisasters = false
    }


    // Chart options
    const chartOptions = () => {
        // Get min and max values across both datasets
        const allValues = [
            ...(selectedFipsData?.data || []).map(item => item.value),
            ...(selectedZipData?.data || []).map(item => item.value)
        ];
        const yMin = Math.min(...allValues);
        const yMax = Math.max(...allValues);

        // Get base data for FEMA annotations
        const baseData = selectedFipsData?.data || selectedZipData?.data || [];
        const sortedData = [...baseData].sort((a, b) => new Date(a.date) - new Date(b.date));

        const formattedStateDisasterAnnotations = createDisasterAnnotations(filteredDisasterDeclarationsState, 'state', yMin, yMax)
        const formattedCountyDisasterAnnotations = createDisasterAnnotations(filteredDisasterDeclarations, 'county', yMin, yMax)
        console.log('formattedStateDisasterAnnotations', formattedStateDisasterAnnotations)
        console.log('formattedCountyDisasterAnnotations', formattedCountyDisasterAnnotations)

        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'bottom',
                    display: false
                },
                tooltip: {
                    callbacks: {
                        title: function (tooltipItems) {
                            const item = tooltipItems[0];
                            const dataIndex = item.dataIndex;
                            const datasetIndex = item.datasetIndex;
                            if (isYearlyView()) {
                                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                    'July', 'August', 'September', 'October', 'November', 'December'];
                                const monthIndex = dataIndex;
                                // If month
                                const geo_name = chartData.datasets[datasetIndex].label;
                                // Get the remainder after dividing monthIndex by 12
                                const year = Math.floor(monthIndex / 12) + 2021;
                                const month = monthIndex % 12;

                                return `${geo_name}\n${monthNames[month]} ${year}`;
                            } else {
                                const sortedData = [...activeDataset].sort((a, b) => {
                                    return new Date(a.date) - new Date(b.date);
                                });
                                console.log('tooltip items', tooltipItems)
                                const dataPoint = sortedData[dataIndex];
                                console.log('datapoint', dataPoint)
                                const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                                    'July', 'August', 'September', 'October', 'November', 'December'];
                                return `${monthNames[dataPoint.month - 1]} ${dataPoint.year}`;
                            }
                        },
                        label: function (context) {
                            const value = context.raw;
                            const formattedValue = formatValue(value, activeDataType, true);
                            console.log('context', context)
                            return `   ${formattedValue}`;
                        }
                    }
                },
                annotation: {
                    annotations: {
                        ...formattedStateDisasterAnnotations,
                        ...formattedCountyDisasterAnnotations,
                        // ...sortedData?.map((dataPoint, index) => {
                        //     const hasDisaster = disaster_declarations_list.some(disaster =>
                        //         disaster.year >= 2021 &&
                        //         disaster.year === dataPoint.year &&
                        //         parseInt(disaster.month) === dataPoint.month
                        //     );

                        //     if (hasDisaster) {
                        //         return {
                        //             [`disaster-${index}`]: {
                        //                 type: 'line',
                        //                 xMin: index,
                        //                 xMax: index,
                        //                 yMin: yMin,
                        //                 yMax: yMax,
                        //                 borderColor: 'rgba(255, 0, 0, 0.2)',
                        //                 borderWidth: 3,
                        //             }
                        //         };
                        //     }
                        //     return null;
                        // }).filter(Boolean).reduce((acc, curr) => ({ ...acc, ...curr }), {})
                    }
                },
                datalabels: {
                    display: false
                }
            },
            scales: {
                y: {
                    beginAtZero: false,
                    grid: {
                        color: 'rgba(200, 200, 200, 0.2)'
                    },
                    ticks: {
                        callback: function (value) {
                            return formatValue(value, activeDataType);
                        }
                    },
                    title: {
                        display: false
                    }
                },
                x: {
                    grid: {
                        color: 'rgba(200, 200, 200, 0.2)'
                    },
                    ticks: {
                        callback: function (val, index) {


                            const monthLabel = chartData?.labels?.[index];
                            const yearLabel = chartData?.yearMarkers?.[index];

                            if (yearLabel !== null) {
                                return [monthLabel, yearLabel];
                            }
                            return monthLabel;
                        },
                        maxRotation: 0,
                        minRotation: 0,
                        font: {
                            size: 11
                        }
                    }
                }
            },
            elements: {
                point: {
                    radius: 3,
                    hoverRadius: 5,
                },
                line: {
                    tension: 0.3
                }
            }
        };
    };

    let summary_text = ''
    switch (selectedDataKey) {
        case 'avg_sale_to_list':
            summary_text = "If you are selling this home, ideally you'd like to see this ratio above 1. It does move reflexively with the market, but it gives us a point in time indicator of whether buyers are chasing owners or vice versa."
            break;
        case 'new_listings_to_sales_ratio':
            summary_text = 'Homes coming onto the market faster than being sold can be a telling signal of climate pushing owners into selling and/or fewer buyers feeling confident enough to purchase.'
            break;
        case 'median_dom':
            summary_text = 'When homes take longer to sell in areas with higher climate exposure, that trend eventually comes with reductions in home values.'
            break;
        case 'median_ppsf':
            summary_text = 'By using price per square foot, we avoid large and small sales skewing our insights, providing a better understand how climate is affecting the market as a whole.'
            break;
        case 'off_market_in_two_weeks':
            summary_text = 'This is the slightly less troubling cousin of Median Days on the Market. When homes are selling quickly despite climate risks, that can be a sign that buyers are not pricing in the risk that they face, or owners are dropping their prices.'
            break;
        case 'price_drops':
            summary_text = 'This is the more troublesome cousin of Sold Above Listing. When you see the percentage of homes with price drops increasing, it usually means buyers are becoming desperate to get out, or owners worried about not getting the values they want.'
            break;
        case 'sold_above_list':
            summary_text = 'When compared against climate factors, this can be an indicator of the values starting to weaken, or conversely, of the market not yet pricing in the risk.'
            break;
        default:
            summary_text = null
            break;
    }

    const citationText = "Housing Market Data Source: Redfin"
    return (
        <div className="ltv-market-signals-section-container">
            <h2 className="ltv-attractiveness-title">{title}</h2>

            <div className={`market-signals-grid ${deviceType}`}>
                <div className="market-signals-buttons">
                    {sortedMarketDataKeysWithScores.map((dataKey, index) => (
                        <button
                            key={index}
                            className={`text-align-center market-signal-button ${selectedDataKey === dataKey.id ? 'active' : ''}`}
                            onClick={() => handleDataKeyChange(dataKey.id)}
                        >
                            <div className={`ltv-market-signals-examining-section-market-datapoint-color-indicator ${dataKey.scoreClass}`}></div>

                            {dataKey.label}
                        </button>
                    ))}
                    {deviceType === 'desktop' &&
                        <span className="data-source-citation text-align-center">{citationText}</span>
                    }

                </div>
                <div className="market-signals-content">

                    {/* {deviceType === 'mobile' && (

                    )} */}

                    <div className={`market-signals-chart-header ${deviceType}`}>
                        {(selectedDataObject && deviceType !== 'mobile') && (
                            <div className={`market-signals-datapoint-summary-text ${deviceType} ${selectedDataObject.scoreClass}`}>
                                <span>{selectedDataObject.scoreText} Likelihood</span>
                            </div>
                        )}

                        <h3 className="market-signals-title">
                            {selectedFipsData?.label || selectedZipData?.label}
                        </h3>
                        <h4 className="market-signals-subtitle">
                            {chart_title_text}
                        </h4>

                        {deviceType === 'mobile' && (
                            <div className="market-summary-trend-toggle-container">
                                {(selectedDataObject) && (
                                    <div className={`market-signals-datapoint-summary-text ${deviceType} ${selectedDataObject.scoreClass}`}>
                                        <span>{selectedDataObject.scoreText} Likelihood</span>
                                    </div>
                                )}

                                {(selectedFipsData || selectedZipData) && (

                                    <div className={`trend-lines-toggle-container ${deviceType}`}>
                                        <span className="trend-lines-toggle-label">Show Trend Lines</span>
                                        <div
                                            className={`trend-lines-toggle ${showTrendLines ? 'active' : ''}`}
                                            onClick={() => setShowTrendLines(!showTrendLines)}
                                        >
                                            <div className="trend-lines-toggle-slider"></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {((selectedFipsData || selectedZipData) && deviceType !== 'mobile') && (

                            <div className={`trend-lines-toggle-container ${deviceType}`}>
                                <span className="trend-lines-toggle-label">Show Trend Lines</span>
                                <div
                                    className={`trend-lines-toggle ${showTrendLines ? 'active' : ''}`}
                                    onClick={() => setShowTrendLines(!showTrendLines)}
                                >
                                    <div className="trend-lines-toggle-slider"></div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={`market-signals-chart-container ${deviceType}`}>
                        {(selectedFipsData || selectedZipData) ? (
                            <Line data={chartData} options={chartOptions()} />
                        ) : (
                            <div className="market-signals-no-data-message">No data available</div>
                        )}
                    </div>
                    <div className={`market-signals-chart-legend ${deviceType}`}>
                        {isThereFipsData &&
                            <>
                                <div className="market-signals-legend-item">
                                    <div className="legend-color" style={{ backgroundColor: 'rgba(75, 192, 192, 1)', border: '2px solid rgba(75, 192, 192, 1)' }}></div>
                                    <span>{chart_legend_fips_text}</span>
                                </div>
                                {showTrendLines &&
                                    <div className="market-signals-legend-item">
                                        <div className="legend-color" style={{ backgroundColor: 'transparent', border: '2px dashed rgba(75, 192, 192, 0.5)' }}></div>
                                        <span>Trend</span>
                                        {/* <span>{chart_legend_fips_trend_text}</span> */}
                                    </div>
                                }
                            </>
                        }
                        {isThereZipData &&
                            <>
                                <div className="market-signals-legend-item">
                                    <div className="legend-color" style={{ backgroundColor: 'rgba(153, 102, 255, 1)', border: '2px solid rgba(153, 102, 255, 1)' }}></div>
                                    <span>{chart_legend_zip_text}</span>
                                </div>
                                {showTrendLines &&
                                    <div className="market-signals-legend-item">
                                        <div className="legend-color" style={{ backgroundColor: 'transparent', border: '2px dashed rgba(153, 102, 255, 0.5)' }}></div>
                                        {/* <span>{chart_legend_zip_trend_text}</span> */}
                                        <span>Trend</span>
                                    </div>
                                }
                            </>
                        }
                        <div className="market-signals-legend-item">
                            <div className="legend-color legend-color-vertical" style={{ backgroundColor: stateDisasterColor, width: '2px', height: '24px' }}></div>
                            <span>Statewide Disaster</span>
                        </div>
                        <>
                            <div className="market-signals-legend-item">
                                <div className="legend-color legend-color-vertical" style={{ backgroundColor: countyDisasterColor, width: '2px', height: '24px' }}></div>
                                <span>{chart_legend_fips_text} Declared Disaster</span>
                            </div>
                        </>
                    </div>
                    {summary_text &&
                        <div className="market-signals-summary-text text-align-center">
                            <p>{summary_text}</p>
                        </div>
                    }
                </div>
                {deviceType !== 'desktop' &&
                    <span className="data-source-citation text-align-center">{citationText}</span>
                }
            </div>
        </div>
    );
};

export default TheSignals;